import { useEffect, type ReactNode } from "react";
import {
  IDIOMAS,
  NOMES_DOS_IDIOMAS,
  idiomaDaEtiqueta,
  idiomaGuardado,
  useIdioma,
} from "../i18n";
import { BANDEIRA, Bandeira } from "./BandeiraDoIdioma";
import { PerfilSchema } from "../online/esquemas";
import { useSessao } from "../online/sessao";

/**
 * Onde se troca de idioma, e o elo entre a escolha e a conta.
 *
 * **A troca mora no canto superior direito da tela inicial.** É a única tela
 * por onde todo mundo passa antes de escolher qualquer coisa, vale para quem
 * não tem conta nenhuma, e é para onde se volta ao sair de uma carreira. Idioma
 * que não se lê é uma armadilha, então o controle é bandeira e não palavra:
 * quem abriu o jogo num idioma que não entende reconhece a própria bandeira sem
 * ler nada.
 *
 * **A tela de opções monta este mesmo componente**, e não um segundo controle.
 * Ela é o endereço de quem procura "onde troco o idioma" depois de já estar
 * jogando; a tela inicial é o de quem ainda nem escolheu clube. São dois
 * lugares e um estado só: a escolha é gravada e levada à conta aqui dentro, em
 * um lugar só, então não existe cópia para as duas telas discordarem.
 *
 * **Não se repete na tela de conta.** A escolha já sobe sozinha para a conta,
 * logo abaixo, e um seletor lá seria um terceiro endereço para a mesma coisa,
 * agora dentro da tela que trata de identidade e não de preferência.
 */
export function SeletorDeIdioma(): ReactNode {
  const { idioma, definirIdioma } = useIdioma();

  return (
    <div
      className="row"
      role="group"
      style={{
        gap: 0,
        // Os três nomes andam juntos: espremido, o seletor esconde justamente o
        // idioma que a pessoa está procurando.
        flexShrink: 0,
        border: "1px solid var(--border-strong)",
        borderRadius: "var(--radius-sm)",
        overflow: "hidden",
      }}
    >
      {IDIOMAS.map((opcao) => {
        const atual = opcao === idioma;
        return (
          <button
            key={opcao}
            lang={opcao}
            aria-pressed={atual}
            // O nome do idioma sai do desenho e vira o nome acessível: sem ele,
            // um leitor de tela anunciaria três botões sem rótulo nenhum.
            aria-label={NOMES_DOS_IDIOMAS[opcao]}
            title={NOMES_DOS_IDIOMAS[opcao]}
            onClick={() => definirIdioma(opcao)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              borderRadius: 0,
              minHeight: 32,
              padding: "0 9px",
              background: atual ? "var(--surface-2)" : "transparent",
              // A bandeira de quem não está escolhido perde um pouco de brilho,
              // e só. Apagada demais ela perde a cor, que é justamente o que se
              // procura quando não se sabe ler a língua da tela.
              opacity: atual ? 1 : 0.75,
            }}
          >
            <Bandeira pais={BANDEIRA[opcao] ?? "BR"} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * O idioma da conta acompanhando o idioma do aparelho, nos dois sentidos.
 *
 * Não desenha nada, como a sincronia da nuvem e a do push. Duas coisas
 * acontecem aqui, e as duas fora de tela:
 *
 * **Aparelho novo herda a escolha da conta.** Quem já escolheu espanhol no
 * celular e entra pela primeira vez no computador não deveria ter que escolher
 * de novo. Só vale quando este aparelho ainda não tem escolha guardada: palpite
 * de servidor não desfaz decisão tomada aqui.
 *
 * **A escolha feita aqui sobe para a conta**, para ser essa herança da próxima
 * vez. `PATCH /v1/me` mistura o que chega com o que já existia, então mandar só
 * o `locale` é suficiente.
 *
 * Falhar é silencioso de propósito: idioma fora de sincronia com o servidor não
 * atrapalha ninguém, e um erro vermelho no meio da tela inicial por causa disso
 * atrapalharia.
 */
export function SincroniaDeIdioma(): null {
  const { idioma, definirIdioma } = useIdioma();
  const sessao = useSessao();
  const daConta = sessao.conta?.usuario.locale ?? null;
  const { cliente, aplicarPerfil } = sessao;

  useEffect(() => {
    if (!daConta) return;
    const doServidor = idiomaDaEtiqueta(daConta);
    if (doServidor === idioma) return;

    if (!idiomaGuardado()) {
      if (doServidor) definirIdioma(doServidor);
      return;
    }

    let vivo = true;
    void (async () => {
      try {
        const perfil = await cliente.pedir(
          { metodo: "PATCH", caminho: "/v1/me", corpo: { locale: idioma } },
          PerfilSchema,
        );
        if (vivo) aplicarPerfil(perfil);
      } catch {
        // Sem rede, sem sessão ou servidor recusando: fica para a próxima.
      }
    })();
    return () => {
      vivo = false;
    };
  }, [daConta, idioma, definirIdioma, cliente, aplicarPerfil]);

  return null;
}
